import { TinyEmitter } from 'tiny-emitter';
import { autoUpdate, offset, flip, shift, hide, arrow, computePosition } from '@floating-ui/dom';
import isEqual from 'lodash.isequal';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

let supportsPassive = false;
if (typeof window !== 'undefined') {
    supportsPassive = false;
    try {
        // @ts-ignore
        window.addEventListener('test', null, Object.defineProperty({}, 'passive', {
            get() { supportsPassive = true; },
        }));
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
    }
    catch (e) { }
}
let isIOS = false;
if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
    // @ts-expect-error MSStream is missing in window type
    isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
class LocalEventHandler {
    constructor() {
        this._listeners = [];
    }
    on(targets, type, handler) {
        this._listeners.push({ targets, type, handler });
        targets.forEach(t => {
            t.addEventListener(type, handler, supportsPassive ? { passive: true } : undefined);
        });
    }
    clear() {
        this._listeners.forEach(({ targets, type, handler }) => {
            targets.forEach(t => t.removeEventListener(type, handler));
        });
        this._listeners = [];
    }
}
class GlobalEventHandler {
    constructor() {
        this._listeners = {
            click: [],
            mousedown: [],
            touchend: [],
        };
        this._started = false;
    }
    on(eventType, handler) {
        if (!this._listeners[eventType].includes(handler)) {
            this._listeners[eventType].push(handler);
        }
    }
    off(eventType, handler) {
        const index = this._listeners[eventType].indexOf(handler);
        if (index !== -1) {
            this._listeners[eventType].splice(index, 1);
        }
    }
    start() {
        if (this._started) {
            return;
        }
        this._started = true;
        const emit = (type) => (event) => this._listeners[type].forEach(fn => fn(event));
        const passive = supportsPassive ? { passive: true, capture: true } : true;
        if (typeof document !== 'undefined' && typeof window !== 'undefined') {
            if (isIOS) {
                document.addEventListener('touchstart', emit('mousedown'), passive);
                document.addEventListener('touchend', emit('touchend'), passive);
            }
            else {
                window.addEventListener('mousedown', emit('mousedown'), true);
                window.addEventListener('click', emit('click'), true);
            }
        }
    }
}
const globalEventHandler = new GlobalEventHandler();
if (typeof window !== 'undefined') {
    globalEventHandler.start();
}

var PLACEMENT;
(function (PLACEMENT) {
    PLACEMENT["LEFT"] = "left";
    PLACEMENT["LEFT_START"] = "left-start";
    PLACEMENT["LEFT_END"] = "left-end";
    PLACEMENT["RIGHT"] = "right";
    PLACEMENT["RIGHT_START"] = "right-start";
    PLACEMENT["RIGHT_END"] = "right-end";
    PLACEMENT["TOP"] = "top";
    PLACEMENT["TOP_START"] = "top-start";
    PLACEMENT["TOP_END"] = "top-end";
    PLACEMENT["BOTTOM"] = "bottom";
    PLACEMENT["BOTTOM_START"] = "bottom-start";
    PLACEMENT["BOTTOM_END"] = "bottom-end";
})(PLACEMENT || (PLACEMENT = {}));

const onClick = (event) => {
    var _a;
    const el = event.currentTarget;
    event.omnicaPopperClose = !el.omnicaPopperCloserTouched;
    event.omnicaPopperCloseAll = !!((_a = el.omnicaPopperCloserModifiers) === null || _a === void 0 ? void 0 : _a.all);
};
const onTouchEnd = ((event) => {
    var _a;
    const el = event.currentTarget;
    const first = el.omnicaPopperCloserTouch;
    delete el.omnicaPopperCloserTouch;
    if (event.changedTouches.length === 1) {
        const touch = event.changedTouches[0];
        event.omnicaPopperClose = (Math.abs(touch.screenY - first.screenY) < 20 &&
            Math.abs(touch.screenX - first.screenX) < 20);
        event.omnicaPopperCloseAll = !!((_a = el.omnicaPopperCloserModifiers) === null || _a === void 0 ? void 0 : _a.all);
    }
});
const onTouchCancel = ((event) => {
    const el = event.currentTarget;
    delete el.omnicaPopperCloserTouch;
});
const onTouchStart = ((event) => {
    if (event.changedTouches.length === 1) {
        const el = event.currentTarget;
        el.omnicaPopperCloserTouch = event.changedTouches[0];
        el.addEventListener('touchend', onTouchEnd);
        el.addEventListener('touchcancel', onTouchCancel);
    }
});
const addCloserListeners = (el) => {
    el.addEventListener('click', onClick);
    el.addEventListener('touchstart', onTouchStart, supportsPassive
        ? { passive: true }
        : false);
};
const removeCloserListeners = (el) => {
    el.removeEventListener('click', onClick);
    el.removeEventListener('touchstart', onTouchStart);
    el.removeEventListener('touchend', onTouchEnd);
    el.removeEventListener('touchcancel', onTouchCancel);
};

const normalizeDelay = (delay) => (Object.assign({ show: 0, hide: 0 }, (typeof delay === 'object' ? delay : {
    show: Number(delay),
    hide: Number(delay),
})));
const nextAnimationFrame = () => __awaiter(void 0, void 0, void 0, function* () { return new Promise(resolve => requestAnimationFrame(resolve)); });
const renameAttribute = (el, src, dst) => {
    if (el.hasAttribute(src)) {
        el.setAttribute(dst, el.getAttribute(src));
        el.removeAttribute(src);
    }
};
const moveArrow = (el, placement, strategy, coords) => {
    const [side] = placement.split('-');
    Object.assign(el.style, {
        position: strategy,
        left: coords.x !== undefined ? `${coords.x}px` : '',
        top: coords.y !== undefined ? `${coords.y}px` : '',
        right: '',
        bottom: '',
        [{
            left: 'right',
            top: 'bottom',
            right: 'left',
            bottom: 'top',
        }[side]]: '-4px',
    });
};
const move = (el, target, options) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const middleware = [];
    if (options.offsetMainAxis || options.offsetCrossAxis) {
        middleware.push(offset({
            mainAxis: Number(options.offsetMainAxis),
            crossAxis: Number(options.offsetCrossAxis),
        }));
    }
    if (options.overflow.includes('flip')) {
        middleware.push(flip({
            boundary: options.boundary,
        }));
    }
    if (options.overflow.includes('shift')) {
        middleware.push(shift({
            boundary: options.boundary,
            padding: 12,
        }));
    }
    if (options.overflow.includes('hide')) {
        middleware.push(hide({ strategy: 'referenceHidden' }));
    }
    const arrow$1 = (_a = el.querySelector('[data-arrow]')) !== null && _a !== void 0 ? _a : null;
    if (arrow$1) {
        middleware.push(arrow({
            element: arrow$1,
            padding: arrow$1.offsetWidth / 3,
        }));
    }
    const { placement, strategy, x, y, middlewareData } = yield computePosition(target, el, {
        middleware,
        placement: options.placement,
        strategy: options.strategy,
    });
    el.style.position = strategy;
    el.style.transform = `translate3d(${Math.round(x)}px,${Math.round(y)}px,0)`;
    if (arrow$1 && middlewareData.arrow) {
        moveArrow(arrow$1, placement, strategy, middlewareData.arrow);
    }
    if (middlewareData.hide) {
        const { referenceHidden } = middlewareData.hide;
        if (referenceHidden) {
            options.onReferenceHidden();
        }
    }
});
class OmnicaPopper {
    constructor(el, target, options) {
        this.autoHide = false;
        this.disposeTimeout = null;
        this._targetTriggers = [];
        this._popperTriggers = [];
        this._container = null;
        this._options = {
            placement: 'bottom',
            boundary: 'clippingAncestors',
            offsetMainAxis: 0,
            offsetCrossAxis: 0,
            overflow: [],
            strategy: 'absolute',
        };
        this._shown = false;
        this._showing = false;
        this._hiding = false;
        this._disposed = false;
        this._clicked = false;
        this._touched = false;
        this._stopAutoUpdate = null;
        this._unsubscribe = () => { };
        this._el = el;
        this._target = target;
        this._targetEvents = new LocalEventHandler();
        this._popperEvents = new LocalEventHandler();
        this._emitter = new TinyEmitter();
        const { targetTriggers, popperTriggers, autoHide, container, delay, disposeTimeout } = options, _options = __rest(options, ["targetTriggers", "popperTriggers", "autoHide", "container", "delay", "disposeTimeout"]);
        this._targetTriggers = targetTriggers !== null && targetTriggers !== void 0 ? targetTriggers : [];
        this._popperTriggers = popperTriggers !== null && popperTriggers !== void 0 ? popperTriggers : [];
        this.autoHide = autoHide !== null && autoHide !== void 0 ? autoHide : false;
        this.disposeTimeout = disposeTimeout !== null && disposeTimeout !== void 0 ? disposeTimeout : null;
        this._container = container !== null && container !== void 0 ? container : null;
        this._delay = normalizeDelay(delay !== null && delay !== void 0 ? delay : 0);
        this.options = _options;
        this._subscribe();
    }
    _subscribe() {
        const onGlobalClick = (event) => this._onGlobalTap(event);
        const onGlobalTouch = (event) => this._onGlobalTap(event, true);
        const onGlobalMousedown = (event) => {
            this._clicked = this._contains(event.target);
        };
        globalEventHandler.on('click', onGlobalClick);
        globalEventHandler.on('mousedown', onGlobalMousedown);
        globalEventHandler.on('touchend', onGlobalTouch);
        this._unsubscribe = () => {
            globalEventHandler.off('click', onGlobalClick);
            globalEventHandler.off('mousedown', onGlobalMousedown);
            globalEventHandler.off('touchend', onGlobalTouch);
        };
    }
    get target() {
        return this._target;
    }
    get container() {
        return this._container;
    }
    get delay() {
        return this._delay;
    }
    set delay(delay) {
        this._delay = normalizeDelay(delay);
    }
    get options() {
        return this._options;
    }
    set options(options) {
        const { offsetMainAxis, offsetCrossAxis } = options, _options = __rest(options, ["offsetMainAxis", "offsetCrossAxis"]);
        this._options = Object.assign(Object.assign(Object.assign({}, this._options), _options), { offsetMainAxis: Number(offsetMainAxis), offsetCrossAxis: Number(offsetCrossAxis) });
    }
    on(event, handler) {
        this._emitter.on(event, handler);
    }
    _emit(event, payload) {
        this._emitter.emit(event, payload);
    }
    initialize(disposed = false) {
        this.detach();
        if (!disposed) {
            this._disposed = false;
            const target = this._target();
            if (target) {
                renameAttribute(target, 'title', 'data-original-title');
            }
            this._startTargetListening();
            this._startPopperListening();
        }
        else {
            this._disposed = true;
        }
    }
    activate(show = false) {
        if (this._disposed) {
            this.initialize();
            if (show) {
                this.show();
            }
        }
    }
    show(immediately = false) {
        if (this._disposed) {
            return;
        }
        this._hiding = false;
        this._schedule(() => __awaiter(this, void 0, void 0, function* () {
            clearTimeout(this._disposeTimer);
            clearTimeout(this._scheduleTimer);
            if (this._shown) {
                return;
            }
            this._teleport();
            yield nextAnimationFrame();
            yield nextAnimationFrame();
            yield this.adjust();
            this.autoUpdateOn();
            if (!this._hiding) {
                this._shown = true;
                this._emit('update:shown', true);
                /** Смена видимости плавающего элемента на отображение на форме */
                this._emit('shown');
            }
        }), immediately ? 0 : this._delay.show);
        /** Появление плавающего элемента */
        this._emit('show');
        this._showing = true;
        requestAnimationFrame(() => setTimeout(() => this._showing = false));
    }
    hide(immediately = false) {
        this._hiding = true;
        this._schedule(() => __awaiter(this, void 0, void 0, function* () {
            clearTimeout(this._scheduleTimer);
            if (!this._shown) {
                return;
            }
            this.autoUpdateOff();
            this._shown = false;
            this._emit('update:shown', false);
            clearTimeout(this._disposeTimer);
            if (this.disposeTimeout !== null) {
                this._disposeTimer = setTimeout(() => {
                    this.detach();
                }, Number(this.disposeTimeout));
            }
            /** Смена видимости плавающего элемента на сокрытие */
            this._emit('hidden');
        }), immediately ? 0 : this._delay.hide);
        /** Сокрытие плавающего элемента */
        this._emit('hide');
    }
    _schedule(operation, delay) {
        clearTimeout(this._scheduleTimer);
        if (delay > 0) {
            this._scheduleTimer = setTimeout(() => operation(), delay);
        }
        else {
            operation();
        }
    }
    changeTarget(target) {
        return __awaiter(this, void 0, void 0, function* () {
            this._target = target;
            this._stopTargetListening();
            this._startTargetListening();
            if (this._shown) {
                this.autoUpdateOn();
                yield this.adjust();
            }
        });
    }
    changeTargetTriggers(triggers) {
        if (!isEqual(this._targetTriggers, triggers)) {
            this._targetTriggers = triggers;
            this._stopTargetListening();
            this._startTargetListening();
        }
    }
    changePopperTriggers(triggers) {
        if (!isEqual(this._popperTriggers, triggers)) {
            this._popperTriggers = triggers;
            this._stopPopperListening();
            this._startPopperListening();
        }
    }
    changeContainer(container) {
        return __awaiter(this, void 0, void 0, function* () {
            this._container = container;
            if (this._shown) {
                this.autoUpdateOff();
                this._teleport();
                yield this.adjust();
                this.autoUpdateOn();
            }
        });
    }
    _startTargetListening() {
        const target = this.target();
        const triggers = this._targetTriggers;
        if (target) {
            this._enableShowTriggers(target, this._targetEvents, triggers);
            this._enableHideTriggers(target, this._targetEvents, triggers.length > 0 ? [...triggers, 'click'] : []);
        }
    }
    _stopTargetListening() {
        this._targetEvents.clear();
    }
    _startPopperListening() {
        const triggers = this._popperTriggers;
        this._enableShowTriggers(this._el, this._popperEvents, triggers);
        this._enableHideTriggers(this._el, this._popperEvents, triggers);
    }
    _stopPopperListening() {
        this._popperEvents.clear();
    }
    _contains(el) {
        return el instanceof Element && this._el.contains(el);
    }
    adjust() {
        return __awaiter(this, void 0, void 0, function* () {
            const target = this._target();
            if (!target || this._disposed) {
                return;
            }
            yield move(this._el, target, Object.assign(Object.assign({}, this._options), { onReferenceHidden: () => this.hide() }));
        });
    }
    autoUpdateOff() {
        if (this._stopAutoUpdate) {
            this._stopAutoUpdate();
            this._stopAutoUpdate = null;
        }
    }
    autoUpdateOn() {
        this.autoUpdateOff();
        const target = this._target();
        if (target) {
            this._stopAutoUpdate = autoUpdate(target, this._el, () => this.adjust(), {
                elementResize: typeof ResizeObserver === 'function',
            });
        }
    }
    detach() {
        var _a;
        (_a = this._el.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(this._el);
    }
    dispose() {
        if (this._disposed) {
            return;
        }
        this._disposed = true;
        this._stopTargetListening();
        this._stopPopperListening();
        this.hide(true);
        this.detach();
        const target = this._target();
        if (target) {
            renameAttribute(target, 'data-original-title', 'title');
        }
        /** Удаление с формы плавающего элемента */
        this._emit('dispose');
    }
    _teleport() {
        var _a;
        if (this._disposed) {
            return;
        }
        const target = this._target();
        if (!target) {
            return;
        }
        const container = typeof this._container === 'string'
            ? document.querySelector(this._container)
            : this._container === null
                ? (_a = target.closest('.omnica-popper-container')) !== null && _a !== void 0 ? _a : document.body
                : this._container;
        if (!container) {
            throw new Error('No container for popper: ' + this._container);
        }
        container.appendChild(this._el);
    }
    destroy() {
        this.autoUpdateOff();
        this._unsubscribe();
        this.dispose();
    }
    _enableShowTriggers(target, events, triggers) {
        triggers.forEach(t => {
            const eventType = OmnicaPopper.SHOW_EVENTS_MAP[t];
            if (eventType) {
                events.on([target], eventType, (event) => {
                    if (!this._shown || this._hiding) {
                        event.processedByOmnicaPopper = true;
                        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                        !this._touched && this.show();
                    }
                });
            }
        });
    }
    _enableHideTriggers(target, events, triggers) {
        triggers.forEach(t => {
            const eventType = OmnicaPopper.HIDE_EVENTS_MAP[t];
            if (eventType) {
                events.on([target], eventType, (event) => {
                    if (!event.processedByOmnicaPopper) {
                        this.hide();
                    }
                });
            }
        });
    }
    _onGlobalTap(event, touch = false) {
        return __awaiter(this, void 0, void 0, function* () {
            const contains = this._clicked || this._contains(event.target);
            yield nextAnimationFrame();
            if (event.omnicaPopperCloseAll || (event.omnicaPopperClose && contains) || (this.autoHide && !contains)) {
                if (this._showing || !this._shown) {
                    return;
                }
                this.hide();
                if (event.omnicaPopperClose) {
                    /** Сокрытие плавающего элемента по клику на элемент с директивой vPopperClose */
                    this._emit('hide:by-closer');
                }
                else {
                    this._emit('hide:by-miss-click');
                }
                if (touch) {
                    this._touched = true;
                    setTimeout(() => this._touched = false, 300);
                }
            }
        });
    }
}
OmnicaPopper.SHOW_EVENTS_MAP = {
    hover: 'mouseenter',
    focus: 'focus',
    click: 'click',
    touch: 'touchstart',
};
OmnicaPopper.HIDE_EVENTS_MAP = {
    hover: 'mouseleave',
    focus: 'blur',
    click: 'click',
    touch: 'touchend',
};

export { OmnicaPopper, PLACEMENT, addCloserListeners, removeCloserListeners };
